import React, { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/components/ui/use-toast";
import { testOneSignalPlugin } from "@/utils/onesignal-test";
import { 
  initializeOneSignal, 
  getNotificationPermissionStatus, 
  setPushNotificationsEnabled,
  syncNotificationPermissions,
  debugTestExternalId,
  debugOneSignalSDK5Methods,
  testSDK5ExternalIdBinding,
  initializeOneSignalWithImmediateExternalId
} from "@/integrations/onesignal";
import { runOneSignalDiagnostics, generateDiagnosticReport } from "@/utils/onesignal-diagnostics";
import { debugNotificationSync, manualFixNotificationStatus, enhancedManualFixNotificationStatus } from "@/utils/notification-debug";
import { runEnhancedOneSignalDebug, generateDebugReport } from "@/utils/onesignal-debug-enhanced";

const TestNotificationsPage: React.FC = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState<string>("");
  const [permissionStatus, setPermissionStatus] = useState<boolean | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  
  // Log test results
  const logToStatus = (message: string) => {
    setStatus(prev => `${message}\n${prev}`);
    
  };
  
  // Initialize page
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      logToStatus("❌ Not running on a native platform");
      return;
    }
    
    logToStatus("🔍 Testing notification functionality...");
    checkPermissionStatus();
  }, []);
  
  const checkPermissionStatus = async () => {
    try {
      setLoading(true);
      const hasPermission = await getNotificationPermissionStatus();
      setPermissionStatus(hasPermission);
      logToStatus(`📱 Permission status: ${hasPermission ? "Granted ✅" : "Not granted ❌"}`);
    } catch (error) {
      logToStatus(`❌ Error checking permission: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleInitialize = async () => {
    try {
      setLoading(true);
      logToStatus("🔄 Initializing OneSignal...");
      await initializeOneSignal();
      logToStatus("✅ OneSignal initialized successfully");
      await checkPermissionStatus();
    } catch (error) {
      logToStatus(`❌ Error initializing: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleEnableNotifications = async () => {
    try {
      setLoading(true);
      logToStatus("🔄 Enabling notifications...");
      const result = await setPushNotificationsEnabled(true);
      logToStatus(`${result ? "✅" : "❌"} Enable result: ${result}`);
      await checkPermissionStatus();
    } catch (error) {
      logToStatus(`❌ Error enabling: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDisableNotifications = async () => {
    try {
      setLoading(true);
      logToStatus("🔄 Disabling notifications...");
      const result = await setPushNotificationsEnabled(false);
      logToStatus(`${result ? "✅" : "❌"} Disable result: ${result}`);
      await checkPermissionStatus();
    } catch (error) {
      logToStatus(`❌ Error disabling: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSync = async () => {
    try {
      setLoading(true);
      logToStatus("🔄 Syncing notification permissions...");
      await syncNotificationPermissions();
      logToStatus("✅ Sync completed");
      await checkPermissionStatus();
    } catch (error) {
      logToStatus(`❌ Error syncing: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDebugSync = async () => {
    try {
      setLoading(true);
      logToStatus("🔍 Running comprehensive notification debug...");
      const result = await debugNotificationSync();
      
      if (result.success) {
        logToStatus("✅ Debug completed successfully");
        logToStatus(`📊 Debug details: ${JSON.stringify(result.details, null, 2)}`);
      } else {
        logToStatus(`❌ Debug failed: ${result.error}`);
      }
    } catch (error) {
      logToStatus(`❌ Error debugging: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleManualFix = async () => {
    try {
      setLoading(true);
      logToStatus("🔧 Attempting manual notification fix...");
      const result = await manualFixNotificationStatus();
      
      if (result.success) {
        logToStatus(`✅ ${result.message}`);
        if (result.details) {
          logToStatus(`📊 Fix details: ${JSON.stringify(result.details, null, 2)}`);
        }
        await checkPermissionStatus();
      } else {
        logToStatus(`❌ Manual fix failed: ${result.message}`);
      }
    } catch (error) {
      logToStatus(`❌ Error with manual fix: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEnhancedManualFix = async () => {
    try {
      setLoading(true);
      logToStatus("🔧 Running enhanced notification permission fix...");
      logToStatus("This will re-request device permissions if needed...");
      
      const result = await enhancedManualFixNotificationStatus();
      
      if (result.success) {
        logToStatus(`✅ ${result.message}`);
        if (result.details) {
          logToStatus(`📊 Fix details: ${JSON.stringify(result.details, null, 2)}`);
        }
        
        toast({
          title: "Fix Successful",
          description: result.message,
          variant: "default"
        });
        
        await checkPermissionStatus();
      } else {
        logToStatus(`❌ Enhanced fix result: ${result.message}`);
        
        if (result.requiresSettingsRedirect) {
          logToStatus("📱 To fix this:");
          logToStatus("1. Go to iOS Settings");
          logToStatus("2. Find your app in the list");
          logToStatus("3. Tap 'Notifications'");
          logToStatus("4. Enable 'Allow Notifications'");
          logToStatus("5. Come back to the app and try again");
          
          toast({
            title: "Permission Required",
            description: "Please enable notifications in iOS Settings",
            variant: "destructive"
          });
        } else {
          toast({
            title: "Fix Failed",
            description: result.message,
            variant: "destructive"
          });
        }
      }
    } catch (error) {
      logToStatus(`❌ Error with enhanced fix: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Error",
        description: "Enhanced fix failed - check logs for details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImprovedSync = async () => {
    try {
      setLoading(true);
      logToStatus("🔧 Running improved notification sync...");
      logToStatus("This handles permission timing issues and post-login sync...");
      
      // Get current user
      const { data: { user } } = await (await import('@/integrations/supabase/client')).supabase.auth.getUser();
      if (!user) {
        logToStatus("❌ No authenticated user found");
        return;
      }
      
      const { syncNotificationState } = await import('@/utils/notification-sync');
      const result = await syncNotificationState(user.id);
      
      if (result.success) {
        logToStatus(`✅ Improved sync completed successfully`);
        logToStatus(`📊 Permission granted: ${result.permissionGranted ? "Yes" : "No"}`);
        logToStatus(`📊 Permission asked: ${result.permissionAsked ? "Yes" : "No"}`);
        logToStatus(`📊 OneSignal ID: ${result.oneSignalUserId ? "Present" : "Missing"}`);
        
        toast({
          title: "Sync Successful",
          description: `Permissions ${result.permissionGranted ? "granted" : "not granted"}`,
          variant: result.permissionGranted ? "default" : "destructive"
        });
        
        await checkPermissionStatus();
      } else {
        logToStatus(`❌ Improved sync failed: ${result.error}`);
        
        toast({
          title: "Sync Failed",
          description: result.error || "Unknown error",
          variant: "destructive"
        });
      }
    } catch (error) {
      logToStatus(`❌ Error with improved sync: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Error",
        description: "Improved sync failed - check logs for details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePostLoginPermissionRequest = async () => {
    try {
      setLoading(true);
      logToStatus("🔔 Testing post-login permission request...");
      logToStatus("This simulates proper permission request after user login...");
      
      const { requestNotificationPermissionForUser } = await import('@/integrations/onesignal');
      const result = await requestNotificationPermissionForUser();
      
      if (result.success) {
        if (result.permissionGranted) {
          logToStatus(`✅ Permission granted successfully!`);
          logToStatus(`📊 OneSignal ID: ${result.oneSignalUserId || "Being generated..."}`);
          
          toast({
            title: "Permission Granted",
            description: "Notifications are now enabled!",
            variant: "default"
          });
        } else {
          logToStatus(`❌ Permission denied by user`);
          
          toast({
            title: "Permission Denied",
            description: "User declined notification permission",
            variant: "destructive"
          });
        }
        
        await checkPermissionStatus();
      } else {
        logToStatus(`❌ Permission request failed: ${result.error}`);
        
        toast({
          title: "Request Failed",
          description: result.error || "Unknown error",
          variant: "destructive"
        });
      }
    } catch (error) {
      logToStatus(`❌ Error with permission request: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Error",
        description: "Permission request failed - check logs for details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOneSignalReady = async () => {
    try {
      setLoading(true);
      logToStatus("🔍 Checking OneSignal readiness...");
      
      const { isOneSignalReady, waitForOneSignalReady, initializeOneSignalWithoutPermission } = await import('@/integrations/onesignal');
      
      // First check if it's ready
      const isReady = await isOneSignalReady();
      logToStatus(`📊 OneSignal ready status: ${isReady ? "✅ Ready" : "❌ Not Ready"}`);
      
      if (!isReady) {
        logToStatus("🔄 OneSignal not ready, attempting to initialize...");
        
        try {
          await initializeOneSignalWithoutPermission();
          logToStatus("✅ OneSignal initialization attempted");
          
          // Wait for it to be ready
          logToStatus("⏳ Waiting for OneSignal to be ready...");
          const nowReady = await waitForOneSignalReady(10000);
          
          if (nowReady) {
            logToStatus("✅ OneSignal is now ready!");
            
            toast({
              title: "OneSignal Ready",
              description: "OneSignal is now initialized and ready",
              variant: "default"
            });
          } else {
            logToStatus("❌ OneSignal still not ready after initialization");
            
            toast({
              title: "OneSignal Not Ready",
              description: "Failed to initialize OneSignal",
              variant: "destructive"
            });
          }
        } catch (initError) {
          logToStatus(`❌ OneSignal initialization failed: ${initError instanceof Error ? initError.message : String(initError)}`);
          
          toast({
            title: "Initialization Failed",
            description: "Could not initialize OneSignal",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "OneSignal Ready",
          description: "OneSignal is already initialized",
          variant: "default"
        });
      }
    } catch (error) {
      logToStatus(`❌ Error checking OneSignal: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Error",
        description: "Failed to check OneSignal status",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckBasicOneSignal = async () => {
    try {
      setLoading(true);
      logToStatus("🔍 Checking basic OneSignal plugin availability...");
      
      // Check environment
      const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;
      logToStatus(`📱 App ID configured: ${appId ? "✅ Yes" : "❌ No"}`);
      if (appId) {
        logToStatus(`📱 App ID: ${appId}`);
      }
      
      // Check window.plugins
      if (typeof window === 'undefined') {
        logToStatus("❌ Window object not available");
        return;
      }
      
      logToStatus(`📱 window.plugins exists: ${!!window.plugins ? "✅ Yes" : "❌ No"}`);
      
      if (window.plugins) {
        const availablePlugins = Object.keys(window.plugins);
        logToStatus(`📱 Available plugins: ${availablePlugins.join(", ")}`);
        
        if (window.plugins.OneSignal) {
          logToStatus("✅ OneSignal plugin found!");
          
          const oneSignalMethods = Object.getOwnPropertyNames(window.plugins.OneSignal);
          logToStatus(`📱 OneSignal methods: ${oneSignalMethods.slice(0, 10).join(", ")}${oneSignalMethods.length > 10 ? "..." : ""}`);
          
          // Check for specific methods
          const expectedMethods = ['setAppId', 'init', 'getDeviceState', 'User', 'Notifications'];
          expectedMethods.forEach(method => {
            const hasMethod = method in window.plugins.OneSignal || typeof window.plugins.OneSignal[method] === 'function';
            logToStatus(`📱 Has ${method}: ${hasMethod ? "✅" : "❌"}`);
          });
          
          // Check SDK version indicators
          if (window.plugins.OneSignal.User && window.plugins.OneSignal.Notifications) {
            logToStatus("📱 SDK Version: ✅ 5.x (Modern)");
          } else if (window.plugins.OneSignal.getDeviceState) {
            logToStatus("📱 SDK Version: ✅ 3.x/4.x (Legacy)");
          } else {
            logToStatus("📱 SDK Version: ❌ Unknown/Incompatible");
          }
          
        } else {
          logToStatus("❌ OneSignal plugin not found!");
          logToStatus("💡 This usually means:");
          logToStatus("   1. Plugin not installed: npm install onesignal-cordova-plugin");
          logToStatus("   2. Capacitor sync needed: npx cap sync");
          logToStatus("   3. Build issue or plugin compatibility problem");
        }
      } else {
        logToStatus("❌ window.plugins not available!");
        logToStatus("💡 This usually means Cordova/Capacitor plugins haven't loaded yet");
      }
      
      // Check Cordova
      logToStatus(`📱 window.cordova exists: ${!!window.cordova ? "✅ Yes" : "❌ No"}`);
      if (window.cordova) {
        logToStatus(`📱 Cordova version: ${window.cordova.version || "Unknown"}`);
      }
      
    } catch (error) {
      logToStatus(`❌ Error checking basic OneSignal: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestPlugin = () => {
    try {
      logToStatus("🔍 Testing OneSignal plugin...");
      testOneSignalPlugin();
      logToStatus("✅ Test function executed - check console for results");
    } catch (error) {
      logToStatus(`❌ Error testing plugin: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const handleRunDiagnostics = async () => {
    try {
      setLoading(true);
      logToStatus("🔍 Running comprehensive diagnostics...");
      
      const report = await generateDiagnosticReport();
      logToStatus("📋 Diagnostic Report:");
      logToStatus(report);
      
      // Also show as toast
      toast({
        title: "Diagnostic Complete",
        description: "Check the status log for detailed results",
        variant: "default"
      });
    } catch (error) {
      logToStatus(`❌ Diagnostic error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEnhancedDebug = async () => {
    try {
      setLoading(true);
      logToStatus("🔍 Running enhanced OneSignal debug...");
      
      const result = await runEnhancedOneSignalDebug();
      const report = generateDebugReport(result);
      
      logToStatus("📋 Enhanced Debug Report:");
      logToStatus(report);
      
      if (result.success) {
        toast({
          title: "Debug Complete - No Issues Found",
          description: "OneSignal appears to be working correctly",
          variant: "default"
        });
      } else {
        toast({
          title: `Debug Complete - ${result.issues.length} Issues Found`,
          description: "Check the status log for detailed results",
          variant: "destructive"
        });
      }
    } catch (error) {
      logToStatus(`❌ Enhanced debug error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestExternalId = async () => {
    try {
      setLoading(true);
      logToStatus("🆔 Testing External ID setting with your real Supabase user ID...");
      logToStatus("This mimics the actual production login flow!");
      
      // First, let's check the user status from this component
      try {
        const { data: { user } } = await (await import('@/integrations/supabase/client')).supabase.auth.getUser();
        if (user && user.id) {
          logToStatus(`✅ User is logged in from test page: ${user.id}`);
          logToStatus(`📊 User email: ${user.email || 'No email'}`);
        } else {
          logToStatus(`❌ No user found from test page`);
          toast({
            title: "Not Logged In",
            description: "Please log in first before testing External ID",
            variant: "destructive"
          });
          return;
        }
      } catch (userCheckError) {
        logToStatus(`❌ Error checking user from test page: ${userCheckError instanceof Error ? userCheckError.message : String(userCheckError)}`);
        return;
      }
      
      const result = await debugTestExternalId();
      
      if (result.success) {
        logToStatus(`✅ ${result.message}`);
        logToStatus(`📊 Your Supabase ID: ${result.details.supabaseUserId}`);
        logToStatus(`📊 External ID set: ${result.details.externalIdSet}`);
        logToStatus(`📊 OneSignal User ID: ${result.details.oneSignalUserId || "Being generated..."}`);
        logToStatus(`📊 Test details: ${JSON.stringify(result.details, null, 2)}`);
        
        toast({
          title: "External ID Test Complete",
          description: "Successfully set your real Supabase ID as External ID!",
          variant: "default"
        });
      } else {
        logToStatus(`❌ External ID test failed: ${result.message}`);
        if (result.details && result.details.userError) {
          logToStatus(`📊 User error details: ${JSON.stringify(result.details.userError, null, 2)}`);
        }
        toast({
          title: "External ID Test Failed", 
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      logToStatus(`❌ External ID test error: ${error instanceof Error ? error.message : String(error)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTestSDK5Methods = async () => {
    try {
      setLoading(true);
      logToStatus("🔍 Testing OneSignal SDK 5.x method compatibility...");
      logToStatus("This will test which methods are available in your OneSignal plugin.");
      
      const result = await debugOneSignalSDK5Methods();
      
      logToStatus(`📊 SDK 5.x Test Result: ${result.success ? "✅ COMPLETED" : "❌ FAILED"}`);
      logToStatus(`📊 Message: ${result.message}`);
      
      if (result.details) {
        const details = result.details;
        logToStatus(`📊 SDK Version: ${details.sdkVersion}`);
        
        if (details.availableMethods) {
          const available = details.availableMethods;
          logToStatus(`📊 Plugin Methods: ${Object.keys(available.pluginMethods).join(', ') || 'None'}`);
          logToStatus(`📊 User Methods: ${Object.keys(available.userMethods).join(', ') || 'None'}`);
          logToStatus(`📊 Notification Methods: ${Object.keys(available.notificationsMethods).join(', ') || 'None'}`);
          logToStatus(`📊 Cordova Methods to Test: ${available.cordovaMethods.join(', ') || 'None'}`);
        }
        
        if (details.testResults) {
          const tests = details.testResults;
          
          if (tests.userLogin) {
            const userLoginResult = tests.userLogin.success ? "✅ WORKS" : "❌ FAILED";
            logToStatus(`📊 User.login() Test: ${userLoginResult}`);
            if (!tests.userLogin.success && tests.userLogin.error) {
              logToStatus(`   Error: ${tests.userLogin.error.message || tests.userLogin.error}`);
            }
          }
          
          if (tests.cordovaLogin) {
            const cordovaLoginResult = tests.cordovaLogin.success ? "✅ WORKS" : "❌ FAILED";
            logToStatus(`📊 Cordova login Test: ${cordovaLoginResult}`);
            if (!tests.cordovaLogin.success && tests.cordovaLogin.error) {
              logToStatus(`   Error: ${tests.cordovaLogin.error.message || tests.cordovaLogin.error}`);
            }
          }
          
          if (tests.cordovaSetExternal) {
            const cordovaSetExternalResult = tests.cordovaSetExternal.success ? "✅ WORKS" : "❌ FAILED";
            logToStatus(`📊 Cordova setExternalUserId Test: ${cordovaSetExternalResult}`);
            if (!tests.cordovaSetExternal.success && tests.cordovaSetExternal.error) {
              logToStatus(`   Error: ${tests.cordovaSetExternal.error.message || tests.cordovaSetExternal.error}`);
            }
          }
        }
        
        // Summary and recommendations
        const workingMethods = [];
        if (details.testResults?.userLogin?.success) workingMethods.push('User.login()');
        if (details.testResults?.cordovaLogin?.success) workingMethods.push('Cordova login');
        if (details.testResults?.cordovaSetExternal?.success) workingMethods.push('Cordova setExternalUserId');
        
        if (workingMethods.length > 0) {
          logToStatus(`📊 Working Methods: ${workingMethods.join(', ')}`);
          logToStatus(`💡 Recommendation: Use any of the working methods above`);
        } else {
          logToStatus(`⚠️ No working methods found - this indicates a plugin compatibility issue`);
          logToStatus(`💡 Recommendation: Check OneSignal plugin version and ensure SDK 5.x compatibility`);
        }
      }
      
      toast({
        title: result.success ? "SDK 5.x Test Complete ✅" : "SDK 5.x Test Failed ❌",
        description: result.message,
        variant: result.success ? "default" : "destructive"
      });
      
    } catch (error) {
      logToStatus(`❌ SDK 5.x test error: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "SDK 5.x Test Failed",
        description: "Could not test OneSignal SDK 5.x methods",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestSDK5ExternalIdBinding = async () => {
    try {
      setLoading(true);
      logToStatus("🧪 Testing NEW SDK 5.x External ID Binding Methods...");
      logToStatus("This will test all the corrected method names for setting external ID");
      
      // Try to get current user ID, but fall back to test ID if not logged in
      let externalIdToTest: string;
      try {
        const { data: { user } } = await (await import('@/integrations/supabase/client')).supabase.auth.getUser();
        if (user && user.id) {
          externalIdToTest = user.id;
          logToStatus(`✅ Using your real Supabase user ID: ${user.id}`);
        } else {
          throw new Error('No user found');
        }
      } catch (userError) {
        // Use a test external ID when not logged in
        externalIdToTest = `test-external-id-${Date.now()}`;
        logToStatus(`⚠️ Not logged in, using test external ID: ${externalIdToTest}`);
        logToStatus("💡 For production testing, please log in first to use your real user ID");
      }
      
      const result = await testSDK5ExternalIdBinding(externalIdToTest);
      
      logToStatus(`📊 External ID Binding Test: ${result.success ? "✅ SUCCESS" : "❌ FAILED"}`);
      logToStatus(`📊 Message: ${result.message}`);
      
      if (result.details) {
        const details = result.details;
        logToStatus(`📊 Initial External ID: ${details.initialExternalId || 'Not Set'}`);
        logToStatus(`📊 Final External ID: ${details.finalExternalId || 'Not Set'}`);
        logToStatus(`📊 Successful Method: ${details.successfulMethod || 'None'}`);
        
        if (details.methodsTested && details.methodsTested.length > 0) {
          logToStatus(`📊 Methods Tested (${details.methodsTested.length}):`);
          details.methodsTested.forEach((test: any) => {
            const status = test.success ? '✅' : '❌';
            const error = test.error ? ` (${test.error})` : '';
            logToStatus(`   ${status} ${test.name}${error}`);
          });
        }
      }
      
      toast({
        title: result.success ? "External ID Binding Success!" : "External ID Binding Failed",
        description: result.message,
        variant: result.success ? "default" : "destructive"
      });
      
    } catch (error) {
      logToStatus(`❌ External ID binding test error: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Test Error",
        description: "Could not run external ID binding test",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestAnonymousPreventionInit = async () => {
    try {
      setLoading(true);
      logToStatus("🚀 Testing Anonymous User Prevention Initialization...");
      logToStatus("This method sets External ID BEFORE OneSignal initialization to prevent anonymous users");
      
      // Get current user
      const { data: { user } } = await (await import('@/integrations/supabase/client')).supabase.auth.getUser();
      if (!user) {
        logToStatus("❌ Must be logged in to test proper initialization");
        toast({
          title: "Login Required",
          description: "Please log in first to test proper initialization",
          variant: "destructive"
        });
        return;
      }

      logToStatus(`✅ Using your Supabase user ID: ${user.id}`);
      logToStatus("🔧 This will prevent creating new anonymous users in OneSignal");
      
      const result = await initializeOneSignalWithImmediateExternalId(user.id);
      
      logToStatus(`📊 Anonymous Prevention Init: ${result.success ? "✅ SUCCESS" : "❌ FAILED"}`);
      logToStatus(`📊 Message: ${result.message}`);
      logToStatus(`📊 Was New User: ${result.wasNewUser ? "Yes" : "No"}`);
      logToStatus(`📊 Permission Granted: ${result.permissionGranted ? "Yes" : "No"}`);
      logToStatus(`📊 OneSignal User ID: ${result.oneSignalUserId || "Not Generated"}`);
      
      if (result.success) {
        logToStatus("✅ SUCCESS! This should prevent duplicate users in OneSignal dashboard");
        await checkPermissionStatus();
        
        toast({
          title: "Initialization Success!",
          description: "OneSignal properly bound without creating anonymous users",
          variant: "default"
        });
      } else {
        toast({
          title: "Initialization Failed",
          description: result.message,
          variant: "destructive"
        });
      }
      
    } catch (error) {
      logToStatus(`❌ Anonymous prevention init error: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Initialization Error",
        description: "Could not test anonymous prevention initialization",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickDiagnosticAndFix = async () => {
    try {
      setLoading(true);
      logToStatus("🚀 Running Quick OneSignal Diagnostic & Fix...");
      logToStatus("This checks if OneSignal is responsive and fixes common issues.");
      
      const { quickOneSignalDiagnosticAndFix } = await import('@/integrations/onesignal');
      const result = await quickOneSignalDiagnosticAndFix();
      
      logToStatus(`📊 Diagnostic Result: ${result.success ? "✅ SUCCESS" : "❌ FAILED"}`);
      logToStatus(`📊 Message: ${result.message}`);
      
      if (result.details) {
        logToStatus(`📊 OneSignal Available: ${result.details.adapterAvailable ? "✅ Yes" : "❌ No"}`);
        logToStatus(`📊 OneSignal Responsive: ${result.details.responsive ? "✅ Yes" : "❌ No"}`);
        
        if (result.details.pluginStructure) {
          logToStatus(`📊 Plugin Structure: User=${result.details.pluginStructure.hasUser}, Notifications=${result.details.pluginStructure.hasNotifications}`);
        }
        
        if (result.details.deviceState) {
          logToStatus(`📊 Device State: UserID=${result.details.deviceState.userId ? "Present" : "Missing"}`);
          logToStatus(`📊 Has Permission: ${result.details.deviceState.hasNotificationPermission ? "✅ Yes" : "❌ No"}`);
          logToStatus(`📊 External ID: ${result.details.deviceState.externalUserId || "Not Set"}`);
        }
        
        if (result.details.externalIdCheck) {
          const check = result.details.externalIdCheck;
          logToStatus(`📊 External ID Check: Expected=${check.expected}, Actual=${check.actual}, Match=${check.matches ? "✅" : "❌"}`);
          
          if (result.details.externalIdFixed !== undefined) {
            logToStatus(`📊 External ID Fix: ${result.details.externalIdFixed ? "✅ Attempted" : "❌ Failed"}`);
          }
        }
        
        if (result.details.reinitResult) {
          logToStatus(`📊 Reinitialization: ${result.details.reinitResult.success ? "✅ Success" : "❌ Failed"}`);
        }
      }
      
      toast({
        title: result.success ? "Diagnostic Complete ✅" : "Issues Found ⚠️",
        description: result.message,
        variant: result.success ? "default" : "destructive"
      });
      
    } catch (error) {
      logToStatus(`❌ Quick diagnostic error: ${error instanceof Error ? error.message : String(error)}`);
      toast({
        title: "Diagnostic Failed",
        description: "Could not run OneSignal diagnostic",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (!Capacitor.isNativePlatform()) {
    return (
      <div className="container py-8 bg-gray-25 min-h-screen">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 max-w-lg mx-auto">
          <h2 className="text-[17px] font-bold text-gray-900 mb-1">Notification Testing</h2>
          <p className="text-[13px] text-gray-500 mb-3">This page only works on native mobile devices.</p>
          <p className="text-[13px] text-gray-600">Please run this test on a physical device or emulator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4 max-h-screen overflow-y-auto bg-gray-25">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm mb-4">
        <div className="p-5 border-b border-gray-100">
          <h2 className="text-[17px] font-bold text-gray-900">OneSignal Notification Testing</h2>
          <p className="text-[12px] text-gray-500 mt-0.5">Test your notification configuration</p>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Button 
              variant="outline" 
              onClick={handleTestPlugin}
              disabled={loading}
            >
              Test Plugin
            </Button>
            <Button 
              variant="outline" 
              onClick={handleRunDiagnostics}
              disabled={loading}
            >
              Run Diagnostics
            </Button>
            <Button 
              variant="outline" 
              onClick={handleEnhancedDebug}
              disabled={loading}
            >
              Enhanced Debug
            </Button>
            <Button 
              variant="outline" 
              onClick={handleInitialize}
              disabled={loading}
            >
              Initialize
            </Button>
            <Button 
              variant="outline" 
              onClick={checkPermissionStatus}
              disabled={loading}
            >
              Check Status
            </Button>
            <Button 
              variant="outline" 
              onClick={handleSync}
              disabled={loading}
            >
              Sync
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleDebugSync}
              disabled={loading}
            >
              Debug Sync
            </Button>
            <Button 
              variant="default" 
              onClick={handleEnableNotifications}
              disabled={loading || permissionStatus === true}
            >
              Enable Notifications
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDisableNotifications}
              disabled={loading || permissionStatus === false}
            >
              Disable Notifications
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleManualFix}
              disabled={loading}
            >
              🔧 DB Fix Only
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleEnhancedManualFix}
              disabled={loading}
              className="col-span-2"
            >
              🔧 Fix Permissions (Recommended)
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleImprovedSync}
              disabled={loading}
              className="col-span-2"
            >
              🔧 Improved Sync
            </Button>
            <Button 
              variant="destructive" 
              onClick={handlePostLoginPermissionRequest}
              disabled={loading}
              className="col-span-2"
            >
              🔔 Post-Login Permission Request
            </Button>
            <Button 
              variant="outline" 
              onClick={handleCheckOneSignalReady}
              disabled={loading}
            >
              Check OneSignal Ready
            </Button>
            <Button 
              variant="outline" 
              onClick={handleCheckBasicOneSignal}
              disabled={loading}
            >
              Check Basic OneSignal
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleTestExternalId}
              disabled={loading}
              className="col-span-2"
            >
              🆔 Test External ID (REAL USER ID)
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleQuickDiagnosticAndFix}
              disabled={loading}
              className="col-span-2"
            >
              🚀 Quick Diagnostic & Fix
            </Button>
            <Button 
              variant="secondary" 
              onClick={handleTestSDK5Methods}
              disabled={loading}
              className="col-span-2"
            >
              🔬 Test SDK 5.x Methods
            </Button>
            <Button 
              variant="default" 
              onClick={handleTestSDK5ExternalIdBinding}
              disabled={loading}
              className="col-span-2"
            >
              🧪 NEW: Test SDK 5.x External ID Fix
            </Button>
            <Button 
              variant="default" 
              onClick={handleTestAnonymousPreventionInit}
              disabled={loading}
              className="col-span-3 bg-green-600 hover:bg-green-700 text-white font-bold"
            >
              🚀 SOLUTION: Prevent Anonymous Users (RECOMMENDED)
            </Button>
          </div>
          
          <div className="pt-2">
            <h3 className="text-[13px] font-semibold text-gray-700 mb-2">Status Log</h3>
            <ScrollArea className="h-[300px] w-full rounded-xl border border-gray-200 bg-gray-25 p-3 font-mono text-[12px] text-gray-700">
              {status || "No logs yet..."}
            </ScrollArea>
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-100">
          <p className="text-[12px] text-gray-500">
            Current permission status: {permissionStatus === null ? "Unknown" : permissionStatus ? "Granted" : "Denied"}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TestNotificationsPage; 